import { z } from 'zod';

// ============================================================
// BLOCK-CONTENT-SCHEMAS
// ============================================================

/**
 * Inhalt eines Text-Blocks.
 * Unterstützt Überschrift, optionale Unterüberschrift und Fließtext (Markdown).
 */
export const TextBlockContentSchema = z.object({
  heading: z.string().min(1).max(200),
  subheading: z.string().max(300).optional(),
  body: z.string().max(10000), // Markdown
});

/**
 * Inhalt eines Bilderstrecken-Blocks.
 * Referenziert MediaFile-IDs aus dem Media-System.
 */
export const GalleryBlockContentSchema = z.object({
  mediaFileIds: z.array(z.string()).min(1).max(20),
  caption: z.string().max(500).optional(),
});

/**
 * Inhalt eines Ansprechpartner-Blocks.
 * Kann entweder einen System-User (Clerk) referenzieren oder freie Kontaktdaten enthalten.
 */
export const ContactBlockContentSchema = z.object({
  name: z.string().min(1).max(200),
  role: z.string().max(200).optional(),
  email: z.string().email().optional(),
  phone: z.string().max(50).optional(),
  avatarMediaFileId: z.string().optional(),
  /** Clerk User ID — überschreibt name/email wenn vorhanden */
  systemUserId: z.string().optional(),
});

/**
 * Inhalt eines kombinierten Text+Bild-Blocks.
 * Bild-Layout (links oder rechts) ist konfigurierbar.
 */
export const CombinedBlockContentSchema = z.object({
  heading: z.string().max(200).optional(),
  body: z.string().max(5000), // Markdown
  mediaFileId: z.string(),
  imageLayout: z.enum(['left', 'right']).default('left'),
});

/**
 * Inhalt eines Video-Blocks.
 * Unterstützt YouTube- und Vimeo-URLs.
 */
export const VideoBlockContentSchema = z.object({
  url: z.string().url(), // YouTube oder Vimeo URL
  title: z.string().max(200).optional(),
});

/**
 * Inhalt eines Zitat-Blocks.
 */
export const QuoteBlockContentSchema = z.object({
  text: z.string().min(1).max(1000),
  author: z.string().min(1).max(200),
  source: z.string().max(300).optional(),
});

/**
 * Inhalt eines FAQ-Blocks.
 * Frage und Antwort (Antwort unterstützt Markdown).
 */
export const FaqBlockContentSchema = z.object({
  question: z.string().min(1).max(500),
  answer: z.string().min(1).max(5000), // Markdown
});

// ============================================================
// BLOCK-TYP-ENUM
// ============================================================

/**
 * Alle erlaubten Block-Typen für die Über-uns-Seite.
 */
export const AboutBlockTypeSchema = z.enum([
  'text',
  'gallery',
  'contact',
  'combined',
  'video',
  'quote',
  'faq',
]);

// ============================================================
// KATEGORIE & BLOCK SCHEMAS
// ============================================================

/**
 * Einzelner Inhalts-Block innerhalb einer Über-uns-Kategorie.
 * Das Feld `content` ist ein raw JSON-String — der Client parsed ihn mit dem typ-spezifischen Schema.
 */
export const AboutBlockSchema = z.object({
  id: z.string(),
  categoryId: z.string(),
  type: AboutBlockTypeSchema,
  /** Raw JSON-String — parsed mit dem passenden Block-Content-Schema */
  content: z.string(),
  sortOrder: z.number().int(),
  lastEditedById: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * Inhaltskategorie für die Über-uns-Seite.
 * Beispiele: "Geschichte der Fanini", "Codex", "Sonstiges"
 */
export const AboutCategorySchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  sortOrder: z.number().int(),
  isPublished: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  blocks: z.array(AboutBlockSchema).optional(),
});

// ============================================================
// REQUEST SCHEMAS
// ============================================================

/**
 * Request-Body zum Erstellen einer neuen Kategorie.
 */
export const CreateAboutCategorySchema = z.object({
  slug: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z0-9-]+$/, 'Slug darf nur Kleinbuchstaben, Ziffern und Bindestriche enthalten'),
  name: z.string().min(1).max(200),
  sortOrder: z.number().int().min(0).optional().default(0),
  isPublished: z.boolean().optional().default(false),
});

/**
 * Request-Body zum Aktualisieren einer Kategorie (alle Felder optional).
 */
export const UpdateAboutCategorySchema = z.object({
  slug: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z0-9-]+$/, 'Slug darf nur Kleinbuchstaben, Ziffern und Bindestriche enthalten')
    .optional(),
  name: z.string().min(1).max(200).optional(),
  sortOrder: z.number().int().min(0).optional(),
  isPublished: z.boolean().optional(),
});

/**
 * Request-Body zum Erstellen eines neuen Blocks.
 * Content wird als JSON-String oder Objekt übergeben und validiert.
 */
export const CreateAboutBlockSchema = z.object({
  type: AboutBlockTypeSchema,
  content: z.string().min(1), // JSON-String
  sortOrder: z.number().int().min(0).optional().default(0),
});

/**
 * Request-Body zum Aktualisieren eines Blocks (alle Felder optional).
 */
export const UpdateAboutBlockSchema = z.object({
  type: AboutBlockTypeSchema.optional(),
  content: z.string().min(1).optional(),
  sortOrder: z.number().int().min(0).optional(),
  isActive: z.boolean().optional(),
});

/**
 * Request-Body zum Neu-Sortieren von Kategorien oder Blöcken.
 * Analog zum Media-Reorder-Pattern.
 */
export const AboutReorderSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.string(),
        sortOrder: z.number().int().min(0),
      }),
    )
    .min(1),
});

// ============================================================
// TYPESCRIPT TYPES
// ============================================================

/** Block-Typ-Enum */
export type AboutBlockType = z.infer<typeof AboutBlockTypeSchema>;

/** Einzelner Block */
export type AboutBlock = z.infer<typeof AboutBlockSchema>;

/** Inhaltskategorie */
export type AboutCategory = z.infer<typeof AboutCategorySchema>;

/** Kategorie mit ihren Blöcken (für öffentliche API-Response) */
export type AboutCategoryWithBlocks = AboutCategory & { blocks: AboutBlock[] };

/** Typed Block-Content-Typen */
export type TextBlockContent = z.infer<typeof TextBlockContentSchema>;
export type GalleryBlockContent = z.infer<typeof GalleryBlockContentSchema>;
export type ContactBlockContent = z.infer<typeof ContactBlockContentSchema>;
export type CombinedBlockContent = z.infer<typeof CombinedBlockContentSchema>;
export type VideoBlockContent = z.infer<typeof VideoBlockContentSchema>;
export type QuoteBlockContent = z.infer<typeof QuoteBlockContentSchema>;
export type FaqBlockContent = z.infer<typeof FaqBlockContentSchema>;

/** Request-Typen */
export type CreateAboutCategory = z.infer<typeof CreateAboutCategorySchema>;
export type UpdateAboutCategory = z.infer<typeof UpdateAboutCategorySchema>;
export type CreateAboutBlock = z.infer<typeof CreateAboutBlockSchema>;
export type UpdateAboutBlock = z.infer<typeof UpdateAboutBlockSchema>;
export type AboutReorder = z.infer<typeof AboutReorderSchema>;

/**
 * Öffentliche API-Response für GET /api/public/about.
 */
export interface PublicAboutResponse {
  categories: AboutCategoryWithBlocks[];
}
